// Style properties the mark `parseHTML` rules read (Bold: `font-weight`,
// Italic: `font-style`, Underline/Strike: `text-decoration`). Google Docs
// expresses bold as `<span style="font-weight:700">` and wraps the paste in
// `<b style="font-weight:normal" id="docs-internal-guid…">`, so these must
// survive normalization — dropping them loses bold and bolds the whole paste.
const SEMANTIC_STYLE_PROPS = ['font-weight', 'font-style', 'text-decoration'] as const;

/**
 * The PRIVATE FORMATTING NORMALIZER for externally pasted HTML (task 4.3's
 * seam; moved to `/core` by task 5.5).
 *
 * **This is not a sanitizer and must never be used as one.** `<script>`, `on*`
 * handlers and `javascript:` hrefs pass straight through; they are neutralized
 * downstream by ProseMirror's schema-driven parse (pinned by the app's paste
 * integration test). The safety-shaped helper on this door is the separately
 * named `sanitizeHtmlAllowlisted` — the two are deliberately distinct, and
 * this one carries a formatting name so a caller reaching for safety cannot
 * pick it up by accident. Do not reuse it outside a paste pipeline.
 *
 * What it does: strips styling cruft from Word / Google Docs / web-page HTML
 * before the schema parse. Removes `class` and every style property except the
 * semantic ones above (so colors and fonts never reach the live editor's
 * transient `TextStyle`/`Color` marks, which `getMarkdown()` would silently
 * drop on save), and unwraps styling-only `<span>`/`<font>` wrappers. Headings
 * are kept — H2–H5 are content headings, and a pasted `<h1>` is reconciled
 * with the plain-text flavor's `sectionNode` by `normalizePastedSections` once
 * the slice is parsed.
 *
 * Calls the DOM at call time (an inert document, never the live one), so
 * importing this module stays SSR-safe; only invoking it needs a browser.
 *
 * The app's markdown-native path re-exports this under its frozen historical
 * name `sanitizePastedHtml`. vMarkdown's same-named helper is a standalone
 * twin with a different signature, not this implementation.
 */
export const normalizePastedFormatting = (html: string): string => {
  // Parse in an inert document (no browsing context) so pasted markup can't
  // execute during normalization (e.g. `<img onerror>`) — mirrors ProseMirror's
  // own clipboard parsing.
  const container = document.implementation.createHTMLDocument('').createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('[style]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    // Probe `getPropertyValue` rather than enumerating declared names: browsers
    // store the `text-decoration` shorthand as its longhands, which would never
    // match the list, but the shorthand getter reconstructs the value.
    const kept = SEMANTIC_STYLE_PROPS.map(
      (prop) => [prop, el.style.getPropertyValue(prop)] as const
    )
      .filter(([, value]) => value)
      .map(([prop, value]) => `${prop}: ${value}`)
      .join('; ');
    if (kept) {
      el.setAttribute('style', kept);
    } else {
      el.removeAttribute('style');
    }
  });

  container.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));

  container.querySelectorAll('span, font').forEach((el) => {
    if (el.tagName === 'SPAN' && el.attributes.length > 0) return;
    const parent = el.parentNode;
    while (el.firstChild) parent?.insertBefore(el.firstChild, el);
    parent?.removeChild(el);
  });

  return container.innerHTML;
};
